// "All data" after the template's 55 columns (customer point 66: "not meant to look nice, it is meant to be a mess
// with columns of all the possible data"): every trial field of the report's field catalog (reportFields.js - a field
// added there comes along by itself), then, for each visit, application and activity that any of the exported trials
// has, its task, Time interval (date and text), Planned, Done, Description, BBCH, Completed by and Cancelled.
// The template's columns stay first and unchanged: the customer's Work_Sorting.py reads them by position.
// The data is a trial row from POST /v1/trials/export-rows (its `fields` and `tasks`).
import { REPORT_FIELDS, formatReportValue, isMissing, reportFieldValue } from '../../components/Reports/reportFields';
import { timeIntervalParts } from '../planning';
import { DATE_FORMAT, cleanCellValue } from './cellStyle';
import { excelDate, pickTask, plannedText } from './taskCells';

/** The trial fields: the catalog's, without the task fields (those are the task columns). */
export const TRIAL_FIELDS = REPORT_FIELDS.filter((def) => !def.key.startsWith('diaries.'));

/** The columns of each task, after its key ("V03 Task", "V03 TI date", ...). */
export const TASK_PARTS = ['Task', 'TI date', 'TI text', 'Planned', 'Done', 'Description', 'BBCH', 'Completed by', 'Cancelled'];

/**
 * A task's column key: V00, V01 ... for a visit ("3" in old data is V03), A ... Z for an application, the VA code
 * (A10, A13 ...) for an activity; null for the old system's other types and numbers without a column.
 */
export function taskKey(task) {
  const vano = String(task?.vano ?? '').trim();
  switch (Number(task?.va_id)) {
    case 1: return /^\d{1,2}$/.test(vano) ? `V${vano.padStart(2, '0')}` : null;
    case 2: return /^[A-Za-z]$/.test(vano) ? vano.toUpperCase() : null;
    case 8: return /^A\d+$/.test(vano) ? vano : null;
    default: return null;
  }
}

// Visits by number, then applications by letter, then activities by code number
const keyOrder = (key) => (key.startsWith('V') ? [0, Number(key.slice(1))] : /^[A-Z]$/.test(key) ? [1, key.charCodeAt(0)] : [2, Number(key.slice(1))]);

/** The task keys any of the trials has, in column order. */
export function usedTaskKeys(trials) {
  const keys = new Set();
  trials.forEach((trial) => (trial.tasks ?? []).forEach((task) => {
    const key = taskKey(task);
    if (key) keys.add(key);
  }));
  return [...keys].sort((a, b) => {
    const [x, y] = [keyOrder(a), keyOrder(b)];
    return x[0] - y[0] || x[1] - y[1];
  });
}

/** The headers after the template's: the trial fields' labels, then "<key> <part>" for each used task key. */
export function allDataHeaders(taskKeys) {
  return [...TRIAL_FIELDS.map((def) => def.label), ...taskKeys.flatMap((key) => TASK_PARTS.map((part) => `${key} ${part}`))];
}

/** A trial field's cell value: a number as a number, a date as a real date, anything else as the report shows it. */
function fieldValue(def, fields) {
  const value = reportFieldValue(def, fields ?? {});
  if (isMissing(value)) return null;
  if (def.type === 'number') {
    const number = Number(value);
    return Number.isFinite(number) ? number : String(value);
  }
  if (def.type === 'date') return excelDate(value) ?? formatReportValue(value, def);
  return formatReportValue(value, def);
}

/** A task's 9 cell values (TASK_PARTS), all empty without the task. */
export function taskValues(task) {
  if (!task) return TASK_PARTS.map(() => null);
  const { date, text } = timeIntervalParts(task);
  const cancelled = task.cancelled ? (task.cancelled_reason ? `Yes: ${task.cancelled_reason}` : 'Yes') : null;
  return [task.task || null, excelDate(date), text || null, plannedText(task), excelDate(task.donedate),
    task.description || null, task.bbch || null, task.completed_by || task.persons || null, cancelled];
}

/** A trial row's values after the template's, for the given task keys. */
export function allDataValues(trial, taskKeys) {
  const byKey = new Map();
  (trial.tasks ?? []).forEach((task) => {
    const key = taskKey(task);
    if (key) byKey.set(key, [...(byKey.get(key) ?? []), task]);
  });
  return [
    ...TRIAL_FIELDS.map((def) => fieldValue(def, trial.fields)),
    ...taskKeys.flatMap((key) => taskValues(byKey.has(key) ? pickTask(byKey.get(key)) : null)),
  ];
}

/**
 * Adds the columns after the template's to "All data", from column `firstColumn` on, for the trial rows (in the
 * sheet's order); plain, with a bold header. Returns the number of columns added.
 */
export function addAllDataColumns(sheet, trials, firstColumn) {
  const taskKeys = usedTaskKeys(trials);
  const headerRow = sheet.getRow(1);
  allDataHeaders(taskKeys).forEach((header, c) => {
    const cell = headerRow.getCell(firstColumn + c);
    cell.value = header;
    cell.font = { bold: true };
  });
  trials.forEach((trial, r) => {
    const row = sheet.getRow(r + 2);
    allDataValues(trial, taskKeys).forEach((value, c) => {
      if (value === null) return;
      const cell = row.getCell(firstColumn + c);
      cell.value = cleanCellValue(value);
      if (value instanceof Date) cell.numFmt = DATE_FORMAT;
    });
  });
  return TRIAL_FIELDS.length + taskKeys.length * TASK_PARTS.length;
}
